import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { StandardFormMetaInfo } from '../standard-form-builder/standard-form-meta-info.model';
import { PageCommandModel } from '../../page-commands/page-command.model';
import { StandardFormTypes } from '../standard-form-builder/standard-form-types.enum';
import { TranslateService } from '@ngx-translate/core';
import { InsparkTemplate } from '@inspark/inspark-components';

export interface ButtonsLabelType {
  reload?: string | boolean;
  save?: string | boolean;
  create?: string | boolean;
  copy?: string | boolean;
  delete?: string | boolean;
  deleteFile?: string | boolean;
  savetitle?: string | boolean;
  operatingmode?: string | boolean;
  timeschedule?: string | boolean;
  upload?: string | boolean;
  view?: string | boolean;
  extcommand?: string | boolean;
  menu?: string | boolean;
  cancel?: string | boolean;
  addinterval?: string | boolean;
  createnew?: string | boolean;
  move?: string | boolean;
}

export const buttonsLabelsDefaults: ButtonsLabelType = {
  reload: 'RELOAD',
  save: 'SAVE',
  create: 'CREATE',
  copy: 'COPY',
  delete: 'DELETE',
  deleteFile: 'DELETE FILE',
  operatingmode: 'OPERATINGMODE',
  timeschedule: 'TIMESCHEDULE',
  upload: 'UPLOAD',
  view: 'VIEW',
  extcommand: 'Ext command',
  menu: 'USER MENU',
  cancel: 'CANCEL',
  addinterval: 'Add Intervel',
  createnew: 'Create',
};

@Component({
  selector: 'app-modern-form-builder',
  templateUrl: './modern-form-builder.component.html',
  styleUrls: ['./modern-form-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModernFormBuilderComponent implements OnInit, OnChanges, AfterViewChecked {
  @Input() isFullHeight = true;
  @Input() isOneColumn = false;
  @Input() pageTitle = '';
  @Input() pageCommands: PageCommandModel[] = [];
  @Input() updatePrivilege? = true;
  @Input() meta: StandardFormMetaInfo[];
  @Input() values: any;
  @Input() itemOptions = {};
  @Input() buttons;
  @Input() showBottomActions: { save: boolean; close: boolean } = { save: false, close: false };
  // tslint:disable-next-line:max-line-length
  @Input() buttonsLabels: ButtonsLabelType = buttonsLabelsDefaults;
  // tslint:disable-next-line:max-line-length
  @Input() buttonsColor: {
    reload?: string;
    save?: string;
    create?: string;
    copy?: string;
    delete?: string;
    deleteFile?: string;
    savetitle?: string;
    operatingmode?: string;
    timeschedule?: string;
    upload?: string;
    view?: string;
    extcommand?: string;
    createnew?: string;
  } =
    // tslint:disable-next-line:max-line-length
    {
      save: 'primary',
      create: 'primary',
      copy: 'primary',
      delete: 'primary',
      deleteFile: 'primary',
      operatingmode: 'primary',
      timeschedule: 'primary',
      upload: 'primary',
      view: 'primary',
      extcommand: 'primary',
    };
  @Input() inAction = false;
  @Input() disabled = {
    reload: false,
    save: false,
    create: false,
    copy: false,
    extcommand: false,
    createnew: false,
  };
  @Input() accept?: string = null;
  @Input() readonlyMode = false;
  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<any>();
  @Output() create = new EventEmitter<any>();
  @Output() duplicate = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() view = new EventEmitter<any>();
  @Output() reload = new EventEmitter<any>();
  @Output() deleteFile = new EventEmitter<any>();
  @Output() operatingmode = new EventEmitter<any>();
  @Output() timeschedule = new EventEmitter<any>();
  @Output() upload = new EventEmitter<any>();
  @Output() addinterval = new EventEmitter<any>();
  @Output() extcommand = new EventEmitter<any>();
  @Output() changed = new EventEmitter<any>();
  @Output() createnew = new EventEmitter<any>();
  @Output() filterParams = new EventEmitter<any>();
  @Output() closeRightPanel = new EventEmitter<void>();
  @Output() resolveParamNames = new EventEmitter<string[]>();
  @ContentChildren(InsparkTemplate) templates: QueryList<InsparkTemplate>;
  checked = false;
  @ViewChild('tooltips', { static: true }) tooltips: ElementRef<any>;
  log = console.log;
  displayName = '';

  constructor(private ngxTranslateService: TranslateService) {}

  ngOnInit() {
    return;
  }

  ngOnChanges() {
    this.setDisplayNameFromMeta();
    this.ngAfterViewChecked();
  }

  ngAfterViewChecked() {
    if (this.tooltips.nativeElement.children.length > 0) {
      const [t1, t2] = this.tooltips.nativeElement.children;

      const t1HasChildren = !!t1 && t1.children.length > 0;
      const t2HasChildren = !!t2 && t2.children.length > 0;

      if (!t1HasChildren && !t2HasChildren) {
        this.buttons = { ...this.buttons, menu: false };
      } else {
        this.buttons = { ...this.buttons, menu: true };
      }
    }
  }

  onSave() {
    this.setDisplayNameFromMeta();
    this.crunchDropInItemType();
    this.save.next(this.values);
  }

  onCreate() {
    this.crunchDropInItemType();
    this.create.next(this.values);
  }

  onCancel() {
    this.cancel.next(this.values);
  }

  onCreateNew() {
    this.createnew.next(this.values);
  }

  onDuplicate() {
    this.duplicate.next(this.values);
  }

  onDelete() {
    this.delete.next(this.values);
  }

  onDeleteFile() {
    this.deleteFile.next(this.values);
  }

  onOperatingMode() {
    this.operatingmode.next(this.values);
  }

  onTimeSchedule() {
    this.timeschedule.next(this.values);
  }

  onUpload(event) {
    this.upload.next(event);
  }

  onView(event) {
    this.view.next(event);
  }

  onAddInterval(event) {
    this.addinterval.next(event);
  }

  onReload(event) {
    this.reload.next(this.values);
  }

  onExtcommand(event) {
    this.extcommand.next(event);
  }

  private setDisplayNameFromMeta() {
    const row = this.meta.find((m) => m.isDisplayName) || this.meta[0];
    if (this.pageTitle === '') {
      if (
        row &&
        (row.type === StandardFormTypes.item || row.type === StandardFormTypes.const) &&
        row.value
      ) {
        try {
          this.displayName = row.value(this.values) + '';
        } catch {
          this.displayName = this.ngxTranslateService.instant('Create');
        }
        return;
      }
      this.displayName = this.values[row.field] || this.ngxTranslateService.instant('Create');
    } else {
      this.displayName = this.pageTitle;
    }
  }

  crunchDropInItemType() {
    const list = this.meta.filter((m) => m.type === 'item');
    if (list) {
      list.forEach((item) => {
        let s = this.values[item.field];
        if (!s || s.isEmpty) {
          this.values[item.field] = null;
        }
      });
    }
  }
}
